// =============================================================================
// Property Vendors: CRUD scoped to property (Settings > Vendors)
// =============================================================================

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateVendorQueries } from "@/lib/mutationHelpers";

export const VENDOR_TYPES = [
  "Plumber",
  "Electrician",
  "HVAC",
  "Handyman",
  "Locksmith",
  "Appliance repair",
  "Pest control",
  "Landscaper/Groundskeeper",
  "Snow removal",
  "Pool/Spa",
  "Junk removal",
  "Painter",
  "Roofer",
  "Window/Door",
  "Internet/TV",
  "Cleaning company",
  "Other",
] as const;

export type VendorType = (typeof VENDOR_TYPES)[number];

export interface PropertyVendor {
  id: string;
  property_id: string;
  business_name: string;
  vendor_type: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const useVendors = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.vendors.byProperty(propertyId || ""),
    queryFn: async (): Promise<PropertyVendor[]> => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("property_vendors")
        .select("*")
        .eq("property_id", propertyId)
        .order("business_name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PropertyVendor[];
    },
    enabled: !!propertyId,
  });
};

export const useCreateVendor = (propertyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      business_name: string;
      vendor_type: VendorType;
      phone?: string | null;
      email?: string | null;
      address?: string | null;
      notes?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("property_vendors")
        .insert({
          property_id: propertyId,
          business_name: input.business_name,
          vendor_type: input.vendor_type,
          phone: input.phone ?? null,
          email: input.email ?? null,
          address: input.address ?? null,
          notes: input.notes ?? null,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;
      return data as PropertyVendor;
    },
    onSuccess: () => {
      invalidateVendorQueries(queryClient, propertyId);
    },
  });
};

export const useUpdateVendor = (propertyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      business_name: string;
      vendor_type: VendorType;
      phone?: string | null;
      email?: string | null;
      address?: string | null;
      notes?: string | null;
    }) => {
      const { id, ...rest } = input;
      const { data, error } = await supabase
        .from("property_vendors")
        .update({
          ...rest,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) throw error;
      return data as PropertyVendor;
    },
    onSuccess: () => {
      invalidateVendorQueries(queryClient, propertyId);
    },
  });
};

export const useDeleteVendor = (propertyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("property_vendors")
        .delete()
        .eq("id", id)
        .eq("property_id", propertyId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateVendorQueries(queryClient, propertyId);
    },
  });
};
